import type { Runners } from '@ps/isp-web-services-types';

import { EventTypeId } from '@swf/sports/types/eventType';

interface Arguments {
  marketType: string;
  runners: Runners;
  eventTypeId: EventTypeId;
}

const marketTypeIs =
  (predicateMarketType: string) =>
  ({ marketType }: Arguments) =>
    marketType === predicateMarketType;

const marketTypeIsOneOf =
  (predicateMarketTypes: string[]) =>
  ({ marketType }: Arguments) =>
    predicateMarketTypes.includes(marketType);

const marketTypeStartsWith =
  (prefix: string) =>
  ({ marketType }: Arguments) =>
    marketType.startsWith(prefix);

const hasTwoRunners = ({ runners }: Arguments) => runners.length === 2;
const hasThreeRunners = ({ runners }: Arguments) => runners.length === 3;

const isFootball = ({ eventTypeId }: Arguments) => eventTypeId === EventTypeId.SOCCER;
const isTennis = ({ eventTypeId }: Arguments) => eventTypeId === EventTypeId.TENNIS;
const isBasketball = ({ eventTypeId }: Arguments) => eventTypeId === EventTypeId.BASKETBALL;
const isWaterPolo = ({ eventTypeId }: Arguments) => eventTypeId === EventTypeId.WATER_POLO;
const isDarts = ({ eventTypeId }: Arguments) => eventTypeId === EventTypeId.DARTS;
const isSnooker = ({ eventTypeId }: Arguments) => eventTypeId === EventTypeId.SNOOKER;
const isBoxing = ({ eventTypeId }: Arguments) => eventTypeId === EventTypeId.BOXING;
const isMixedMartialArts = ({ eventTypeId }: Arguments) =>
  eventTypeId === EventTypeId.MIXED_MARTIAL_ARTS;

/**
 * Headers configurations
 */
const headers = [
  {
    header: ['HOME', 'DRAW', 'AWAY'],
    rules: [
      isFootball,
      marketTypeIsOneOf([
        'WIN-DRAW-WIN',
        'HANDICAP_BETTING',
        'ALTERNATIVE_HANDICAPS',
        'ALTERNATIVE_HANDICAPS.',
        'ALTERNATIVE_HANDICAPS..',
        'RESULT_&_BOTH_TO_SCORE',
        'MATCH_HANDICAP_WITH_TIE',
        'EXTRA_TIME_BETTING',
        'FIRST_HALF_HANDICAP_WITH_TIE',
        'SECOND_HALF_HANDICAP_WITH_TIE'
      ])
    ]
  },
  {
    header: ['HOME', 'AWAY'],
    rules: [isFootball, marketTypeIsOneOf(['DRAW_NO_BET', 'TO_QUALIFY', 'TO_LIFT_THE_TROPHY'])]
  },
  {
    header: ['HOME', 'AWAY'],
    rules: [isFootball, marketTypeStartsWith('WIN_&_OVER_')]
  },
  {
    header: ['OVER', 'UNDER'],
    rules: [isFootball, marketTypeStartsWith('OVER_UNDER_')]
  },
  {
    header: ['YES', 'NO'],
    rules: [
      isFootball,
      marketTypeIsOneOf([
        'BOTH_TEAMS_TO_SCORE',
        'BOTH_TEAMS_TO_SCORE_TWO_OR_MORE_GOALS',
        'BOTH_TEAMS_SCORE_NO_DRAW',
        'BOTH_TEAMS_TO_SCORE_SECOND_HALF',
        'BOTH_TEAMS_TO_SCORE_IN_THE_FIRST_HALF'
      ])
    ]
  },
  {
    header: ['1X', 'X2', '12'],
    rules: [isFootball, marketTypeIs('DOUBLE_CHANCE')]
  },

  {
    header: ['HOME', 'TIE', 'AWAY'],
    rules: [marketTypeIsOneOf(['FOURBALLS', 'FOURSOMES', 'WINNER', 'SINGLES'])]
  },

  // Match Filters Tennis Headers
  {
    header: ['YES', 'NO'],
    rules: [
      isTennis,
      marketTypeIsOneOf(['BOTH_PLAYERS_TO_WIN_A_SET_(YES/NO)', 'PLAYER_A_TO_WIN_AT_LEAST_1_SET'])
    ]
  },
  {
    header: ['1', '2'],
    rules: [isTennis, marketTypeIs('MATCH_BETTING')]
  },

  // Match Filters BasketBall Headers
  {
    header: ['OVER', 'UNDER'],
    rules: [
      isBasketball,
      marketTypeIsOneOf([
        'TOTAL_POINTS_(OVER/UNDER)',
        'HOME_TEAM_TOTAL_POINTS',
        'AWAY_TEAM_TOTAL_POINTS'
      ])
    ]
  },

  {
    header: ['HOME', 'DRAW', 'AWAY'],
    rules: [isWaterPolo, marketTypeIs('WIN-DRAW-WIN')]
  },

  // Match Filters Darts Headers
  {
    header: ['1', '2'],
    rules: [isDarts, marketTypeIs('MATCH_BETTING')]
  },

  // Match Filters Snooker Headers
  {
    header: ['1', '2'],
    rules: [isSnooker, marketTypeIs('MATCH_BETTING')]
  },

  // Match Filters Boxing Headers
  {
    header: ['1', 'X', '2'],
    rules: [isBoxing, marketTypeIs('BOXING_MATCH_BETTING')]
  },

  // Match Filters Mixed Martial Arts Headers
  {
    header: ['1', '2'],
    rules: [isMixedMartialArts, marketTypeIs('MATCH_BETTING')]
  },

  // Markets shown with one column layout don't need sub-headers
  {
    header: [],
    rules: [marketTypeIsOneOf(['2_BALLS', '3_BALLS'])]
  },

  // Fallbacks
  {
    header: ['1', 'X', '2'],
    rules: [hasThreeRunners]
  },
  {
    header: ['1', '2'],
    rules: [hasTwoRunners]
  },
  {
    header: [],
    rules: [() => true]
  }
];

export default function buildMarketHeadersFor({
  marketType,
  runners,
  eventTypeId
}: Arguments): string[] {
  const matchingHeader = headers.find((header) =>
    header.rules.every((rule) => rule({ marketType, runners, eventTypeId }))
  );

  return matchingHeader?.header ?? [];
}
